import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppListing } from "../../components/appListing";
import { documentsApi } from "../../redux/service/documents_service";
import { toast } from "react-toastify";
import { IoMdEye } from "react-icons/io";
import moment from "moment";
import MainPageDialog from "../../components/mainPageDialog";
import { getFileUrl } from "../care_plan/constant";
import { getLoggedinUser } from "../../userStore";
import { getBaseUrl } from "../../utils/common_lib";

function Documents() {
  const [documentsListingTrigger] =
    documentsApi.endpoints.getDocuments.useLazyQuery();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);

  const user = getLoggedinUser();
  const authToken = user?.token;
  const practice_id = user?.practice_id;
  const pid = user?.pid;

  const [showDocumentModal, setShowDocumentModal] = useState({
    show: false,
    uuid: "",
    file_type: "",
    file_name: "",
    document_id: "",
    iframe_id: "",
    iframe_name: "",
    document_url: "",
  });

  function getCurrentEpochTime() {
    const currentEpochMillis = Date.now();
    const currentEpochSeconds = Math.floor(currentEpochMillis / 1000);
    return {
      milliseconds: currentEpochMillis,
      seconds: currentEpochSeconds,
    };
  }

  const iFrameIDs = {
    pdf: "printDiv",
    text: "documentViewIframe",
    image: "documentViewIframe",
    audio: "documentViewIframe",
  };

  const getFileType = (mime_type) => {
    if (mime_type.startsWith("image/")) {
      return "image";
    } else if (mime_type.startsWith("application/pdf")) {
      return "pdf";
    } else if (mime_type.startsWith("video/")) {
      return "video";
    } else if (mime_type.startsWith("audio/")) {
      return "audio";
    } else {
      return "text";
    }
  };

  const createDocumentUrl = (document_uuid, type, document_id) => {
    let url = "";
    let baseUrl = getBaseUrl();
    let documentBaseUrl = `${baseUrl}/documents/view.json?_t=${
      getCurrentEpochTime()?.milliseconds
    }&token=${authToken}&practice_id=${practice_id}&pid=${pid}&tz=-240`;

    if (type === "audio") {
      url = `https://view.officeapps.live.com/op/embed.aspx?src=${documentBaseUrl}&id=${document_id}
      `;
    } else if (type === "text") {
      url = `${baseUrl}/utilities/get_attachment.json?_t=${
        getCurrentEpochTime()?.milliseconds
      }&token=${authToken}&practice_id=${practice_id}&pid=${pid}&tz=-240&attachment=${document_uuid}`;
    } else if (type === "video") {
      url = `${documentBaseUrl}&id=${document_id}`;
    } else if (type === "pdf") {
      url = getFileUrl(document_uuid);
    } else if (type === "image") {
      url = `${documentBaseUrl}&id=${document_id}`;
    }
    return url;
  };

  const previewData = useCallback((item) => {
    let file_type = getFileType(item?.mime_type);

    setShowDocumentModal({
      show: true,
      uuid: item?.uuid,
      file_type,
      file_name: item?.file_title,
      document_id: item?.id,
      iframe_id: iFrameIDs[file_type],
      iframe_name: iFrameIDs[file_type],
      document_url: createDocumentUrl(item?.uuid, file_type, item?.id),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await documentsListingTrigger({
        shared_to_patient_portal: 1,
        by_date_order: "desc",
        flat_docs: true,
      });
      if (response?.data?.data) {
        setDocuments(response.data.data);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents", error);
      toast.error("Error fetching documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [documentsListingTrigger]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  function formatBytes(bytes) {
    if (bytes < 1024) {
      return bytes + " Bytes";
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + " KB";
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    }
  }

  const columns = useMemo(
    () => [
      { title: "Title", key: "title" },
      { title: "Type", key: "category_title" },
      { title: "Description", key: "description" },
      {
        title: "Uploaded by",
        key: "added_by_user",
      },
      {
        title: "Date",
        key: "date",
        getData: (item) => moment(item.date).format("MM/DD/YYYY"),
      },
      {
        title: "Size",
        key: "file_size",
        getData: (item) => formatBytes(item?.file_size),
      },
      {
        title: "Preview",
        key: "",
        getData: (item) => (
          <div className="flex w-full items-center justify-center px-2">
            <IoMdEye
              onClick={() => previewData(item)}
              className="cursor-pointer"
              color="#0070e5"
            />
          </div>
        ),
      },
    ],
    [previewData]
  );

  const footerList = [
    {
      title: "Close",
      type: "cancel",
      action: () => {
        setShowDocumentModal(false);
      },
    },
  ];

  return (
    <>
      {showDocumentModal?.show && (
        <MainPageDialog
          title={showDocumentModal?.file_name || "Document"}
          modalSize="md"
          maxHeight={true}
          footerList={footerList}
          onCancelHandler={() =>
            setShowDocumentModal({
              show: false,
              uuid: "",
              file_type: "",
              file_name: "",
              document_id: "",
              iframe_id: "",
              iframe_name: "",
              document_url: "",
            })
          }
        >
          <div
            dangerouslySetInnerHTML={{
              __html: `<iframe id="${showDocumentModal.iframe_id}" name="${showDocumentModal.iframe_name}" width="100%" height="1100" scrolling="auto" frameborder="1" allow="autoplay" src=${showDocumentModal?.document_url}></iframe>`,
            }}
          />
        </MainPageDialog>
      )}
      <div className="flex border-gray-300 bg-gray-200 px-3 py-3 sm:px-4 flex-wrap items-center justify-between sm:flex-nowrap">
        <div className="flex gap-5 items-center">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Documents
          </h3>
        </div>
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={fetchDocuments}
            className="relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Reload
          </button>
        </div>
      </div>
      <div className="flex-auto bg-white justify-start p-3 text-start">
        <AppListing loading={loading} listData={documents} cols={columns} />
      </div>
    </>
  );
}

export default React.memo(Documents);
